/** Stable identity for one client-facing, platform-native conversation surface. */
export type ConversationSurfaceId = `surface_${string}`;

/** Stable identity for one bounded teaching episode. */
export type TeachingEpisodeId = `teach_${string}`;

export const TEACHING_EPISODE_STATES = [
  "active",
  "summarizing",
  "awaiting_confirmation",
  "queued",
  "processing",
  "pr_open",
  "merged",
  "declined",
  "needs_attention",
  "cancelled",
  "expired",
] as const;

export type TeachingEpisodeState = (typeof TEACHING_EPISODE_STATES)[number];

export const TEACHING_STATUS_LABELS = {
  queued: "Lesson queued",
  processing: "Preparing brain proposal",
  pr_open: "Proposal opened for review",
  merged: "Accepted into Ezra's brain",
  declined: "Proposal declined",
  needs_attention: "Needs attention",
} as const satisfies Partial<Record<TeachingEpisodeState, string>>;

export const TEACHING_REFUSAL_REASONS = [
  "teaching_disabled",
  "teaching_not_authorized",
  "teaching_service_not_allowed",
  "teaching_overlay_unavailable",
  "invalid_surface_id",
  "invalid_teaching_episode_id",
  "teaching_episode_not_found",
  "teaching_episode_conflict",
  "teaching_episode_state_conflict",
  "teaching_summary_stale",
  "teaching_summary_generation_failed",
  "teaching_brain_unlinked",
  "teaching_brain_not_writable",
  "teaching_brain_target_changed",
  "teaching_proposal_failed",
] as const;

export type TeachingRefusalReason = (typeof TEACHING_REFUSAL_REASONS)[number];

export interface TeachingSpan {
  ordinal: number;
  conversationId: string;
  startAfterItemId?: string;
  endAtItemId?: string;
  /** Terminal zero-width range (cancelled/expired before any teaching item). */
  empty?: true;
}

export interface ApprovedTeachingSummary {
  version: number;
  hash: string;
  assistantItemId: string;
  text: string;
  /** Structured server-generated meaning behind the conversational rendering. */
  draft?: TeachingSummaryDraft;
}

export interface TeachingProposalStatus {
  state: "open" | "merged" | "declined";
}

/** Public/read DTO. Brain target and teacher identity are deliberately absent. */
export interface TeachingEpisode {
  schemaVersion: 1;
  episodeId: TeachingEpisodeId;
  surfaceId: ConversationSurfaceId;
  state: TeachingEpisodeState;
  spans: TeachingSpan[];
  summary?: ApprovedTeachingSummary;
  proposal?: TeachingProposalStatus;
  failureCode?: TeachingRefusalReason;
  createdAt: string;
  updatedAt: string;
  confirmedAt?: string;
  expiresAt: string;
}

export interface TeachingEpisodesResponse {
  episodes: TeachingEpisode[];
}

export interface TeachingTurnContext {
  episodeId: TeachingEpisodeId;
  surfaceId: ConversationSurfaceId;
}

/** Self-declared teacher attribution; there is no login behind it. */
export interface TeachingAttribution {
  name?: string;
  note?: string;
}

export interface ConfirmTeachingEpisodeRequest {
  summaryVersion: number;
  summaryHash: string;
  /** Self-declared teacher attribution; there is no login behind it. */
  teacher?: TeachingAttribution;
}

export const TEACHING_LEARNING_OUTCOMES = ["memory", "correction", "skill-seed"] as const;
export type TeachingLearningOutcome = (typeof TEACHING_LEARNING_OUTCOMES)[number];

/** Server-generated structured content rendered under the conversational “What I learned” card. */
export interface TeachingSummaryDraft {
  wrongOrMissing: string;
  desiredUnderstanding: string;
  appliesWhen: string[];
  exceptions: string[];
  likelyOutcome: TeachingLearningOutcome[];
  unresolved: string[];
}

export function isConversationSurfaceId(value: unknown): value is ConversationSurfaceId {
  return typeof value === "string" && /^surface_[A-Za-z0-9_-]+$/.test(value);
}

export function isTeachingEpisodeId(value: unknown): value is TeachingEpisodeId {
  return typeof value === "string" && /^teach_[A-Za-z0-9_-]+$/.test(value);
}

export function isTeachingEpisodeState(value: unknown): value is TeachingEpisodeState {
  return typeof value === "string" && (TEACHING_EPISODE_STATES as readonly string[]).includes(value);
}
